import {
  ALL_PATIENT_EVENT_VMS,
  PATIENT_EVENT_CODES,
  PatientEventName,
} from '@/view-model/patient-event-constants/MemberPatientEvents'
import { readableTimeSince } from '@/utils/time'
import { getSingularOrPlural, withSingularOrPluralLabel } from '@/utils/labels'
import PatientStatsMapping from '@/components/patient-stats/PatientStatsMapping.vue'
import { PatientWithMappingReportsAttributes } from '@/jsonapi/PatientWithMappingReports'
import { PatientWithStatsActionNames } from '@/store/modules/patient-with-stats/keys'
import { PatientWithStatsAttributes } from '@/jsonapi/PatientWithStats'
import { ONE_HOUR_TIMESTAMP } from '@/components/patient-stats/graphs/mapping-chart-options/constants'
import { DataPoint } from '@/jsonapi/CommonTypes'
import { PatientWithEventsEvent } from '@/jsonapi/PatientWithEvents'

interface StatStatus {
  name: string
  icon: string
}

export const STAT_STATUSES: Record<string, StatStatus> = {
  Good: {
    name: 'Good',
    icon: 'status-icons/check.svg', // assets/status-icons/
  },
  Attention: {
    name: 'Attention',
    icon: 'status-icons/warning-alert.svg',
  },
  Critical: {
    name: 'Critical',
    icon: 'status-icons/red-alert.svg',
  },
  Unknown: {
    name: 'Unknown',
    icon: 'status-icons/unknown-alert.svg',
  },
}

interface ResidentStatType {
  icon: string
  name: string
}

export const RESIDENT_STAT_TYPES: Record<string, ResidentStatType> = {
  Fall: {
    name: 'Risk of fall',
    icon: 'summary-report-icons/fall.svg',
  },
  UTI: {
    name: 'Risk of UTI',
    icon: 'summary-report-icons/uti.svg',
  },
  Wake: {
    name: 'Awakenings',
    icon: 'summary-report-icons/wake.svg',
  },
  Sleep: {
    name: 'Sleep status',
    icon: 'summary-report-icons/sleep.svg',
  },
  Walk: {
    name: 'Step counts',
    icon: 'summary-report-icons/walk.svg',
  },
  Bedsore: {
    name: 'Risk of bedsores',
    icon: 'summary-report-icons/bedsore.svg',
  },
}

export interface ResidentStat {
  statType: ResidentStatType
  status: StatStatus
  title: string
  subtitle?: string
  detailLink?: string
}

function loadingStatOfType(type: ResidentStatType): ResidentStat {
  return {
    statType: type,
    status: STAT_STATUSES.Unknown,
    title: 'Loading ' + type.name.toLowerCase() + '...',
  }
}
function unknownStatOfType(type: ResidentStatType): ResidentStat {
  return {
    statType: type,
    status: STAT_STATUSES.Unknown,
    title: type.name + ' unknown',
  }
}

// note: also defined in kalamalka:PatientEventSignsAndSymptoms.kt :(
const MEDIUM_FALL_RISK_EVENTS = [
  PatientEventName.SLEEP_CHANGE_CONSOLIDATED,
  PatientEventName.INTERRUPTION_IN_SLEEP_CONSOLIDATED,
  PatientEventName.WALKING_AT_NIGHT, // live
  PatientEventName.REALTIME_SLEEP_INTERRUPTION, // live
]

// so we don't load fall detection, bed sore detection etc. that's not live
const HISTORICAL_EVENTS_FILTER = [
  PatientEventName.SLEEP_CHANGE_CONSOLIDATED,
  PatientEventName.INTERRUPTION_IN_SLEEP_CONSOLIDATED,
]

function lastStatValue(dps, raw = false) {
  if (!dps || dps.length == 0) {
    return 0
  }
  let values = raw ? dps : dps.map(dp => dp.value)
  return values[values.length - 1] || 0
}
function averageStatValue(dps, raw = false) {
  if (!dps || dps.length == 0) {
    return 0
  }

  let values = raw ? dps : dps.map(dp => dp.value)
  return Math.round(values.reduce((total, value) => total + value) / values.length) || 0
}

// might want to pre sort events, but the amount is probably just a handful.
function findMatchingEvent(
  reversedPatientEvents: PatientWithEventsEvent[],
  targetEventNames: string[]
): PatientWithEventsEvent | null {
  for (let e of targetEventNames) {
    let matching = reversedPatientEvents.filter(e2 => e2.eventName == e)
    if (matching && matching.length > 0) {
      return matching[0]
    }
  }
  return null
}

// todo - clean this up. Possibly generate in Kalamalka or Lambda
export function generateStat(
  patient,
  patientWithStats: PatientWithStatsAttributes,
  patientWithMappingReport: PatientWithMappingReportsAttributes,
  statType: ResidentStatType
): ResidentStat {
  if (patient && !patient.lastTrackerConnectionTimestamp) {
    return unknownStatOfType(statType)
  }
  /*
    First, combine events
      (Before March 18 2021, live events weren't returned by patient mapping API,
      and only the latest most recent event is returned by simple-patients).

      Todo... clean this up

  */
  let historicalEvents: PatientWithEventsEvent[] =
    (patientWithMappingReport &&
      patientWithMappingReport.patientEvents &&
      patientWithMappingReport.patientEvents
        .filter(dp => dp.value in HISTORICAL_EVENTS_FILTER)
        .map(
          dp =>
            ({
              eventCategory: 'Sleep',
              eventFamily: 'sleep-change',
              eventName: PATIENT_EVENT_CODES[dp.value],
              eventTimestamp: dp.timestamp,
              eventSeverity: 3, // for now, again
              eventCode: dp.value,
            } as PatientWithEventsEvent)
        )) ||
    []

  let liveEvents: PatientWithEventsEvent[] = (patient && patient.liveEvents) || []

  // always include latest "active" event
  if (patient && patient.patientEvent) {
    historicalEvents.push(patient.patientEvent)
  }
  historicalEvents = historicalEvents.reverse() // get most recent first

  // Awakenings
  let averageAwakenings = averageStatValue(patientWithStats && patientWithStats.numberOfAwakenings)
  let lastAwakenings = lastStatValue(patientWithStats && patientWithStats.numberOfAwakenings)

  // Step counts
  let averageSteps = averageStatValue(patientWithStats && patientWithStats.stepCounts)
  let lastSteps = lastStatValue(patientWithStats && patientWithStats.stepCounts)

  // Sleep length
  let sleepingHours =
    patientWithMappingReport &&
    patientWithMappingReport.sleepingPeriods &&
    patientWithMappingReport.sleepingPeriods.length > 0 &&
    patientWithMappingReport.sleepingPeriods.map(period =>
      Math.round((period.awakeTime - period.sleepingTime) / ONE_HOUR_TIMESTAMP)
    )

  let averageSleepingLength = averageStatValue(sleepingHours, true)
  let lastSleepingLength = lastStatValue(sleepingHours, true)

  switch (statType) {
    case RESIDENT_STAT_TYPES.Fall: {
      if (!patient || !patientWithMappingReport) {
        return loadingStatOfType(statType)
      }

      let matchingEvent = findMatchingEvent(liveEvents, [PatientEventName.FALL_DETECTED])
      if (matchingEvent) {
        return {
          statType: statType,
          status: STAT_STATUSES.Critical,
          title: 'Fall detected ' + readableTimeSince({ timestamp: matchingEvent.eventTimestamp }),
        }
      }
      let matchingFallRiskHistoricalEvent = findMatchingEvent(historicalEvents, MEDIUM_FALL_RISK_EVENTS)
      let matchingFallRiskLiveEvent = findMatchingEvent(liveEvents, MEDIUM_FALL_RISK_EVENTS)

      if (matchingFallRiskHistoricalEvent) {
        return {
          statType: statType,
          status: STAT_STATUSES.Attention,
          title: 'Increased risk of fall.',
          subtitle: ALL_PATIENT_EVENT_VMS[matchingFallRiskHistoricalEvent.eventName] + ' detected', // i.e. "Sleep onset/offset change"
          detailLink: '/patient-stats/' + patient.id + '/mapping',
        }
      } else if (matchingFallRiskLiveEvent) {
        return {
          statType: statType,
          status: STAT_STATUSES.Attention,
          title: 'Increased risk of fall.',
          subtitle: ALL_PATIENT_EVENT_VMS[matchingFallRiskLiveEvent.eventName], // i.e. "Sleep interrupted"
          // detailLink: 'patient-stats/' + patient.id + '/mapping', // no details for live events at this time
        }
      } else {
        return {
          statType: statType,
          status: STAT_STATUSES.Good,
          title: 'Risk of fall is low.',
        }
      }
    }
    case RESIDENT_STAT_TYPES.UTI:
      return {
        statType: statType,
        status: STAT_STATUSES.Good,
        title: 'TEST: Risk of UTI is low.',
      }
    case RESIDENT_STAT_TYPES.Bedsore: {
      // this one is only live.
      if (!patient) {
        return loadingStatOfType(statType)
      }
      if (findMatchingEvent(liveEvents, [PatientEventName.BED_SORE])) {
        return {
          statType: statType,
          status: STAT_STATUSES.Attention,
          title: 'Increased risk of bedsores.',
          subtitle: ALL_PATIENT_EVENT_VMS[PatientEventName.BED_SORE],
          // no details for bedsores (live event) yet
        }
      } else {
        return {
          statType: statType,
          status: STAT_STATUSES.Good,
          title: 'Risk of bedsores is low.',
        }
      }
    }
    case RESIDENT_STAT_TYPES.Wake:
      if (!patientWithStats) {
        return loadingStatOfType(statType)
      }
      // todo: determine threshold for warning
      // assuming last dp is yesterday
      return {
        statType: statType,
        status: lastAwakenings > averageAwakenings ? STAT_STATUSES.Attention : STAT_STATUSES.Good,
        title:
          'Woke up (' +
          (lastAwakenings || 0) +
          ') ' +
          getSingularOrPlural(lastAwakenings || 0, 'time', 'times') +
          ' last night.',
        subtitle:
          'Average: ' +
          (averageAwakenings || 0) +
          ' ' +
          getSingularOrPlural(averageAwakenings, 'time', 'times') +
          ' per night',
        detailLink: '/patient-stats/' + patient.id + '/mapping',
      }
    case RESIDENT_STAT_TYPES.Sleep:
      if (!patientWithMappingReport) {
        return loadingStatOfType(statType)
      }
      return {
        statType: statType,
        status: lastSleepingLength < averageSleepingLength ? STAT_STATUSES.Attention : STAT_STATUSES.Good,
        title: 'Slept (' + lastSleepingLength + ') hours last night.',
        subtitle: 'Average: ' + averageSleepingLength + ' hours per night',
        detailLink: '/patient-stats/' + patient.id + '/mapping',
      }
    case RESIDENT_STAT_TYPES.Walk:
      if (!patientWithStats) {
        return loadingStatOfType(statType)
      }
      // todo, refine this logic. May be different thresholds per patient
      // assuming last dp is yesterday
      return {
        statType: statType,
        status: (lastSteps || 0) < averageSteps ? STAT_STATUSES.Attention : STAT_STATUSES.Good,
        title:
          'Walked (' + (lastSteps || 0) + ') ' + getSingularOrPlural(lastSteps || 0, 'step', 'steps') + ' yesterday.',
        subtitle:
          'Average: ' + (averageSteps || '0') + ' ' + getSingularOrPlural(averageSteps, 'step', 'steps') + ' per day',
        detailLink: '/patient-stats/' + patient.id + '/detailed',
      }
  }
}
